import 'leaflet-draw';
import { Feature } from 'geojson';
import leaflet, { Control, ControlOptions, DivIcon, DrawMap, LatLng, Map, PolylineOptions } from 'leaflet';
import { action, makeObservable } from 'mobx';
import { MapControl, withLeaflet } from 'react-leaflet';
import { calculateCurrentPosition } from '../../../helpers/geometry/mapExtensions.helpers';
import { keyboardShortcutsServiceInstance } from '../../../services/keyboardShortcuts.service';
import { IAnnotationService } from '../annotation.service';
import {
  FREE_DRAW_TOOLS,
  IBrushTypeOptions,
  ICON_MARKER_SIZE,
  ISegmentation,
  ISegmentationFeature,
  MOUSE_BUTTONS,
  MOUSE_EVENTS,
} from '../annotations.interface';
import { DRAW_CONTROL_MODE } from '../containers/Drawing.container';
import { IFreeDrawSegmentationService } from '../freeDrawSegmentation.service';
import { AnnotationToolType } from '../submodules/annotationTypes/models/annotationToolType';
import { IAnnotationTypeOptions } from '../submodules/annotationTypes/models/annotationTypeOptions';
import { IUndoRedoHistory } from '../undoRedoHistory.service';
import { DrawBrush } from './DrawControl.models/Brush/Draw.Brush';
import { DragEx } from './DrawControl.models/DragEx';
import { IDrawer } from './DrawControl.models/IDrawer';
import { createDrawer } from './DrawerFactory';
import { DrawingEditor } from './DrawingEditor';

interface IOuterProps {
  activeAnnotationTypeId: string | undefined;
  annotationService: IAnnotationService;
  areCursorGuidesEnabled: boolean;
  color: string;
  controlMode: DRAW_CONTROL_MODE;
  fillOpacity: number;
  freeDrawFeature?: ISegmentationFeature;
  freeDrawInProgress: boolean;
  freeDrawSegmentationService: IFreeDrawSegmentationService;
  isImprovedVisibilityCursorEnabled: boolean;
  selectorType: AnnotationToolType | null;
  stopActions: boolean;
  undoRedoHistory: IUndoRedoHistory;
  activeAnnotationTypeOptions?: IAnnotationTypeOptions;
  cursorPosition?: LatLng;
  selectedSegmentation?: ISegmentation;
  onDrawCopied: (id: string) => void;
  onDrawCreated: (geojson: Feature) => void;
  onDrawEdited: (index: string | undefined, geojson: Feature) => Promise<void>;
  onDrawPasted: (geojson: Feature, id: string) => void;
  onDrawingRemove: (id: string) => Promise<void>;
  onEditDisabled: () => void;
  onEditEnabled: () => void;
  onFreeDrawCancel: () => Promise<boolean> | undefined;
  onFreeDrawDelete: () => void;
  onFreeDrawStarted: (geojson: Feature) => void;
  onMouseMove: (latlng?: LatLng) => void;
}

interface IProps extends IOuterProps, ControlOptions {
  leaflet: { map: Map };
}

class DrawControlPure extends MapControl<IProps> {
  private drawControl!: leaflet.Control.Draw;
  private drawer!: IDrawer | null;
  private map!: Map;
  private drawingEditor: DrawingEditor | undefined = undefined;
  private frontVerticalGuide: L.Polyline | undefined = undefined;
  private backgroundVerticalGuide: L.Polyline | undefined = undefined;
  private frontHorizontalGuide: L.Polyline | undefined = undefined;
  private backgroundHorizontalGuide: L.Polyline | undefined = undefined;
  private currentCursorPosition: LatLng | undefined;

  constructor(props: IProps) {
    super(props);
    makeObservable(this);
  }

  get frontGuidesOptions(): PolylineOptions {
    return {
      className: 'cursor-guide',
      color: '#888888',
      dashArray: '4, 4',
      dashOffset: '4',
      opacity: 1.0,
      weight: 1,
      pane: 'markerPane',
    };
  }

  get backwardGuidesOptions(): PolylineOptions {
    return {
      className: 'cursor-guide',
      color: '#ffffff',
      dashArray: '4, 4',
      dashOffset: '0',
      opacity: 1.0,
      weight: 1,
      pane: 'markerPane',
    };
  }

  createLeafletElement(): leaflet.Control.Draw {
    leaflet.Editable.VertexIcon = DivIcon.extend({
      options: {
        iconSize: new leaflet.Point(ICON_MARKER_SIZE, ICON_MARKER_SIZE),
        className: 'custom-edit-icon',
      },
    }) as unknown as DivIcon;

    this.drawControl = new leaflet.Control.Draw({
      draw: false,
      editable: true,
      draggable: true,
    } as unknown as Control.DrawConstructorOptions);

    return this.drawControl;
  }

  componentDidMount(): void {
    if (super.componentDidMount) {
      super.componentDidMount();
    }
    this.map = this.props.leaflet.map;
    this.map.keyboard.disable();

    this.setDrawer(this.props);
    this.setDrawingEditor(this.props);

    this.addHandlers();

    if (this.props.controlMode === DRAW_CONTROL_MODE.CREATE) {
      this.disableEdit();
      this.enableDrawing();
    } else {
      this.disableDrawing();
    }

    this.setDragging(this.props.selectorType);
  }

  UNSAFE_componentWillUpdate(newProps: IProps): void {
    this.setDrawer(newProps, false);

    if (!newProps.selectedSegmentation) {
      this.disableEdit();
    }

    if (newProps.stopActions && !this.props.stopActions) {
      this.disableEdit();
      this.disableDrawing();
    }

    if (newProps.controlMode === DRAW_CONTROL_MODE.CREATE) {
      this.disableEdit();
      this.enableDrawing();
    } else {
      this.disableDrawing();
    }

    this.setDragging(newProps.selectorType);
  }

  componentWillUnmount(): void {
    this.disableDrawing();
    this.setDragging(null);
    if (this.drawer) {
      this.drawer.removeHandlers();
    }
    if (this.drawingEditor) {
      this.drawingEditor.removeHandlers();
    }
    this.removeHandlers();
    this.setDefaultCursor();
  }

  setDrawingEditor(props: IProps): void {
    if (this.drawingEditor) {
      this.drawingEditor.removeHandlers();
    }

    this.drawingEditor = new DrawingEditor(
      props.leaflet.map,
      {
        onDrawCopied: props.onDrawCopied,
        onDrawPasted: props.onDrawPasted,
        onDrawEdited: props.onDrawEdited,
        onDrawingRemove: props.onDrawingRemove,
        onEditEnabled: props.onEditEnabled,
        onEditDisabling: this.handleEditDisabling,
        onEditDisabled: this.handleEditDisabled,
      },
      this.props.undoRedoHistory
    );
    this.drawingEditor.addHandlers();
  }

  setDrawer(props: IProps, reset: boolean = true): void {
    if (
      reset ||
      this.props.activeAnnotationTypeId !== props.activeAnnotationTypeId ||
      (props.freeDrawFeature?.id !== this.props.freeDrawFeature?.id && !props.freeDrawInProgress)
    ) {
      if (this.drawer) {
        this.drawer.disable();
        this.drawer.removeHandlers();
        this.drawer = null;
      }

      if (props.selectorType) {
        this.drawer = createDrawer(
          this.map as DrawMap,
          props.selectorType,
          props.color,
          props.fillOpacity,
          props.activeAnnotationTypeId,
          props.cursorPosition,
          this.handleDrawCreated,
          props.onFreeDrawStarted,
          props.undoRedoHistory,
          props.annotationService,
          props.freeDrawSegmentationService,
          props.activeAnnotationTypeOptions,
          props.freeDrawFeature
        );
      }

      if (this.drawer) {
        this.drawer.disableMouseMarkerFocus();
        this.drawer.addHandlers();
        this.drawer.enable();
      }
    } else if (this.drawer) {
      switch (props.selectorType) {
        case AnnotationToolType.BRUSH: {
          const brush = this.drawer as DrawBrush;
          const options = props.activeAnnotationTypeOptions as IBrushTypeOptions;
          brush.updateOptions(options.brushDiameter, options.brushMode, props.fillOpacity);
          break;
        }
        case null:
        case AnnotationToolType.SELECT:
        case AnnotationToolType.POINT:
        case AnnotationToolType.POLYGON:
        case AnnotationToolType.RECTANGLE:
        case AnnotationToolType.VECTOR:
        case AnnotationToolType.POLYLINE:
        case AnnotationToolType.ROTATEDRECTANGLE:
        default:
          break;
      }
    }

    this.setCursorType(props.isImprovedVisibilityCursorEnabled, props.selectorType);

    if (!this.currentCursorPosition) {
      this.currentCursorPosition = props.cursorPosition;
    }

    this.clearGuides();
    const shouldDrawGuides = props.selectorType !== null && props.selectorType !== AnnotationToolType.SELECT && this.props.areCursorGuidesEnabled;
    if (shouldDrawGuides) {
      this.drawGuides();
    }
  }

  setCursorType(isImprovedVisibilityCursorEnabled: boolean, selectorType: AnnotationToolType | null): void {
    this.setDefaultCursor();

    if (!selectorType) {
      return;
    }

    if (selectorType === AnnotationToolType.BRUSH) {
      leaflet.DomUtil.addClass(this.map._container, 'no-cursor-enabled');
      return;
    }

    const cursorClass = isImprovedVisibilityCursorEnabled ? 'custom-cursor-enabled' : 'crosshair-cursor-enabled';
    leaflet.DomUtil.addClass(this.map._container, cursorClass);
  }

  setDefaultCursor(): void {
    leaflet.DomUtil.removeClass(this.map._container, 'crosshair-cursor-enabled');
    leaflet.DomUtil.removeClass(this.map._container, 'custom-cursor-enabled');
    leaflet.DomUtil.removeClass(this.map._container, 'no-cursor-enabled');
  }

  setDragging(selectorType: AnnotationToolType | null): void {
    this.map.dragging.removeHooks?.();
    this.map.addHandler('dragging', DragEx);
    this.map.options.draggingButtons = MOUSE_BUTTONS.MIDDLE;
    if (selectorType === null) {
      this.map.options.draggingButtons = this.map.options.draggingButtons | MOUSE_BUTTONS.LEFT;
    }
    this.map.dragging.addHooks?.();
    this.map.dragging.enable();
  }

  disableDrawing(): void {
    if (this.drawer) {
      this.drawer.disable();
    }
    this.clearGuides();
  }

  enableDrawing(): void {
    if (this.drawer) {
      this.drawer.enable();
    }
  }

  disableEdit(): void {
    if (this.drawingEditor) {
      this.drawingEditor.disableEdit();
      this.drawingEditor.currentlyEditing = { polygon: undefined, edited: false };
    }
  }

  addHandlers(): void {
    document.addEventListener('keydown', this.handleKeyboardDownEvents);
    document.addEventListener(MOUSE_EVENTS.MOUSE_MOVE, this.handleDocumentMouseMove);

    keyboardShortcutsServiceInstance.registerKeyUp('z', (e) => {
      this.handleUndo(e);
    });
    keyboardShortcutsServiceInstance.registerKeyUp('y', (e) => {
      this.handleRedo(e);
    });
    keyboardShortcutsServiceInstance.registerKeyDown('delete', () => {
      this.handleDelete();
    });
  }

  removeHandlers(): void {
    document.removeEventListener('keydown', this.handleKeyboardDownEvents);
    document.removeEventListener(MOUSE_EVENTS.MOUSE_MOVE, this.handleDocumentMouseMove);

    keyboardShortcutsServiceInstance.unregisterKeyUp('z', (e) => {
      this.handleUndo(e);
    });
    keyboardShortcutsServiceInstance.unregisterKeyUp('y', (e) => {
      this.handleRedo(e);
    });
    keyboardShortcutsServiceInstance.unregisterKeyDown('delete', () => {
      this.handleDelete();
    });
  }

  handleDocumentMouseMove = (e: Event): void => {
    this.currentCursorPosition = calculateCurrentPosition(e as MouseEvent, this.map);
    this.props.onMouseMove(this.currentCursorPosition);
    this.refreshGuides();
  };

  refreshGuides(): void {
    this.clearGuides();
    const shouldDrawGuides = this.props.selectorType !== null && this.props.selectorType !== AnnotationToolType.SELECT && this.props.areCursorGuidesEnabled;
    if (shouldDrawGuides) {
      this.drawGuides();
    }
  }

  drawGuides(): void {
    if (!this.currentCursorPosition) {
      return;
    }

    this.backgroundVerticalGuide = new leaflet.Polyline(
      [new leaflet.LatLng(-30000, this.currentCursorPosition.lng), new leaflet.LatLng(30000, this.currentCursorPosition.lng)],
      this.backwardGuidesOptions
    );
    this.backgroundVerticalGuide.addTo(this.map);

    this.frontVerticalGuide = new leaflet.Polyline(
      [new leaflet.LatLng(-30000, this.currentCursorPosition.lng), new leaflet.LatLng(30000, this.currentCursorPosition.lng)],
      this.frontGuidesOptions
    );
    this.frontVerticalGuide.addTo(this.map);

    this.backgroundHorizontalGuide = new leaflet.Polyline(
      [new leaflet.LatLng(this.currentCursorPosition.lat, -30000), new leaflet.LatLng(this.currentCursorPosition.lat, 30000)],
      this.backwardGuidesOptions
    );
    this.backgroundHorizontalGuide.addTo(this.map);

    this.frontHorizontalGuide = new leaflet.Polyline(
      [new leaflet.LatLng(this.currentCursorPosition.lat, -30000), new leaflet.LatLng(this.currentCursorPosition.lat, 30000)],
      this.frontGuidesOptions
    );
    this.frontHorizontalGuide.addTo(this.map);
  }

  private clearGuides(): void {
    this.frontVerticalGuide?.removeFrom(this.map);
    this.backgroundVerticalGuide?.removeFrom(this.map);
    this.frontHorizontalGuide?.removeFrom(this.map);
    this.backgroundHorizontalGuide?.removeFrom(this.map);
  }

  handleDrawCreated = (geojson: Feature): void => {
    this.props.onDrawCreated(geojson);

    if (this.props.selectorType && FREE_DRAW_TOOLS.includes(this.props.selectorType)) {
      return;
    }

    this.setDrawer(this.props);
    this.setDragging(this.props.selectorType);
  };

  handleEditDisabling = async (): Promise<boolean> => {
    return !!this.props.selectorType && FREE_DRAW_TOOLS.includes(this.props.selectorType) && !(await this.props.onFreeDrawCancel());
  };

  handleEditDisabled = (): void => {
    this.props.onEditDisabled();
  };

  @action
  handleUndo(e: KeyboardEvent): void {
    if (e.ctrlKey || e.metaKey) {
      this.props.undoRedoHistory.undo();
    }
  }

  @action
  handleRedo(e: KeyboardEvent): void {
    if (e.ctrlKey || e.metaKey) {
      this.props.undoRedoHistory.redo();
    }
  }

  handleKeyboardDownEvents = (e: KeyboardEvent): void => {
    switch (e.key) {
      case 'Escape':
        this.setDrawer(this.props);
        setTimeout(() => {
          this.enableDrawing();
        }, 200);
        break;
    }
  };

  @action.bound
  handleDelete(): void {
    if (!!this.props.selectorType && FREE_DRAW_TOOLS.includes(this.props.selectorType) && this.props.selectedSegmentation && this.props.freeDrawFeature) {
      this.props.onFreeDrawDelete();
      void this.props.onDrawingRemove(this.props.selectedSegmentation.feature.id);
      this.props.undoRedoHistory.addCommand(this.props.undoRedoHistory.getNewChangeStateCommand());
    }
  }
}

export const DrawControl = withLeaflet(DrawControlPure);
